import { useEffect, useMemo } from 'react'

import { useRestrictedDonationConfig } from '../useRestrictedDonationConfig/useRestrictedDonationConfig'
import { useRestrictedVolunteeringConfig } from '../useRestrictedVolunteeringConfig/useRestrictedVolunteeringConfig'
import { useAllowlistedVolunteeringCountries } from '../useAllowlistedVolunteeringCountries/useAllowlistedVolunteeringCountries'

import { BlockedRegionsConfig } from '@percent/workplace-giving/api/donation/getDonationPartnerConfig/getDonationPartnerConfig.types'
import { OrgSearchFor } from '@percent/workplace-giving/api/search/searchOrganisations/searchVerifiedOrganisations.types'
import {
  donationAllowedOrgCountriesAlpha3,
  volunteeringAllowedOrgCountriesAlpha3
} from '@percent/workplace-giving/common/data/sanctionedCountries'

export const useOrgSearchCountries = (searchFor: OrgSearchFor) => {
  const {
    blockedRegions: donationBlockedRegions,
    allowlistedCountryCodes: donationAllowlistedCountryCodes,
    isBlockedRegionsLoading: isDonationBlockedRegionsLoading
  } = useRestrictedDonationConfig()
  const { blockedRegions: volunteeringBlockedRegions, isBlockedRegionsLoading: isVolunteeringBlockedRegionsLoading } =
    useRestrictedVolunteeringConfig({
      enabled: searchFor !== OrgSearchFor.Donation
    })
  const {
    allowlistedCountryCodes: volunteeringAllowlistedCountryCodes,
    isAllowlistedCountriesLoading: isVolunteeringAllowlistLoading
  } = useAllowlistedVolunteeringCountries({
    enabled: searchFor !== OrgSearchFor.Donation
  })

  const donationBlockedCountryCodes = useMemo(
    () => (donationBlockedRegions ?? []).map((region: BlockedRegionsConfig) => region.countryCode),
    [donationBlockedRegions]
  )
  const volunteeringBlockedCountryCodes = useMemo(
    () => (volunteeringBlockedRegions ?? []).map((region: BlockedRegionsConfig) => region.countryCode),
    [volunteeringBlockedRegions]
  )

  const allowedCountryCodes = useMemo(() => {
    const resolveAllowed = (base: string[], blockedCountryCodes: string[], allowlistedCountryCodes: string[]) =>
      base.filter(code => !blockedCountryCodes.includes(code) || allowlistedCountryCodes.includes(code))

    if (searchFor === OrgSearchFor.Donation)
      return resolveAllowed(
        donationAllowedOrgCountriesAlpha3,
        donationBlockedCountryCodes,
        donationAllowlistedCountryCodes
      )

    if (searchFor === OrgSearchFor.Volunteering)
      return resolveAllowed(
        volunteeringAllowedOrgCountriesAlpha3,
        volunteeringBlockedCountryCodes,
        volunteeringAllowlistedCountryCodes
      )

    const unionBlockedCountryCodes = Array.from(
      new Set([...donationBlockedCountryCodes, ...volunteeringBlockedCountryCodes])
    )
    const unionAllowlistedCountryCodes = Array.from(
      new Set([...donationAllowlistedCountryCodes, ...volunteeringAllowlistedCountryCodes])
    )
    const donationCountryCodes = resolveAllowed(
      donationAllowedOrgCountriesAlpha3,
      unionBlockedCountryCodes,
      unionAllowlistedCountryCodes
    )
    const volunteeringCountryCodes = resolveAllowed(
      volunteeringAllowedOrgCountriesAlpha3,
      unionBlockedCountryCodes,
      unionAllowlistedCountryCodes
    )

    return Array.from(new Set([...donationCountryCodes, ...volunteeringCountryCodes]))
  }, [
    searchFor,
    donationBlockedCountryCodes,
    volunteeringBlockedCountryCodes,
    donationAllowlistedCountryCodes,
    volunteeringAllowlistedCountryCodes
  ])

  const isLoading =
    isDonationBlockedRegionsLoading ||
    (searchFor !== OrgSearchFor.Donation && (isVolunteeringAllowlistLoading || isVolunteeringBlockedRegionsLoading))

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return

    const reEnabledByDonation = donationAllowlistedCountryCodes.filter(code =>
      donationBlockedCountryCodes.includes(code)
    )
    const reEnabledByVolunteering = volunteeringAllowlistedCountryCodes.filter(code =>
      volunteeringBlockedCountryCodes.includes(code)
    )

    if (reEnabledByDonation.length > 0 || reEnabledByVolunteering.length > 0) {
      const donationPart =
        reEnabledByDonation.length > 0 ? ` Donation allowlist: ${reEnabledByDonation.join(', ')}.` : ''
      const volunteeringPart =
        reEnabledByVolunteering.length > 0 ? ` Volunteering allowlist: ${reEnabledByVolunteering.join(', ')}.` : ''

      // eslint-disable-next-line no-console
      console.warn(
        `[useOrgSearchCountries] Countries re-enabled via allowlist override. Ensure the partner's category config includes these countries or org search will return empty results.${donationPart}${volunteeringPart}`
      )
    }
  }, [
    donationBlockedCountryCodes,
    volunteeringBlockedCountryCodes,
    donationAllowlistedCountryCodes,
    volunteeringAllowlistedCountryCodes
  ])

  return { allowedCountryCodes, isLoading }
}
